import React, { useEffect, useState } from 'react';
import Button3D from '../ui/Button3D';
import StatusChip from '../ui/StatusChip';
import { POPUP_DISMISS_MS } from '../../lib/alertCopy';
import NotificationKindIcon from './NotificationKindIcon';

// BL-177 Step 2d (board 02): the in-app pop-up while someone is working in IntelAgent but not on that conversation.
// Bottom-right, 8 seconds, Open + Reply + ×; afterwards it simply lives in the bell (still unread).
// BL-177 Step 2e: restyled to the 3D pressable standard: a raised card with a depth shadow, and 3D Open / Reply buttons
// (pressed = sinks 3 px, shadow to 1 px, 120 ms). Exo 2, white text. Styles: .ia-popup3d in src/styles/step2d.css.
// BL-177 Step 2o: `stacked` = one card inside the pop-up stack (the stack positions it and owns its timeout, so a new message joining the
// card restarts the clock). A card that holds several messages from one conversation carries its count in the title ("3 new messages from …").
export default function NotificationPopup({ view, onOpen, onReply, onDismiss, onOpenMessage, durationMs = POPUP_DISMISS_MS, stacked = false, timerKey = '' }) {
    const [expanded, setExpanded] = useState(false);
    useEffect(() => {
        if (!view) return undefined;
        const timer = setTimeout(() => onDismiss?.('timeout'), durationMs);
        return () => clearTimeout(timer);
        // Alone, the timer restarts whenever the alert changes. Stacked, only when the card is updated (timerKey), never on a re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, stacked ? [timerKey, durationMs] : [view, durationMs, onDismiss]);

    if (!view) return null;
    const count = view.count > 1 ? view.count : 1;
    return (
        <div
            className={`ia-d2 ia-popup3d rounded-2xl p-4 ${stacked ? 'w-full' : 'fixed bottom-4 right-4 z-50 w-[min(26rem,calc(100vw-2rem))]'}`}
            role="status"
            aria-live="polite"
            aria-label="New notification"
            data-testid="notification-popup"
            data-popup-count={count}
            onClick={() => { if (count > 1) setExpanded(true); }}
        >
            <div className="flex items-start gap-3">
                <NotificationKindIcon kind={view.kind} />
                <div className="min-w-0 flex-1">
                    {count > 1 && stacked ? (
                        <button type="button" className="truncate text-left text-base font-bold text-white underline-offset-2 hover:underline"
                            aria-expanded={expanded} aria-controls={`notification-messages-${timerKey}`}
                            data-testid="notification-group-toggle" onClick={(event) => { event.stopPropagation(); setExpanded((current) => !current); }}>
                            {view.title}
                        </button>
                    ) : <p className="truncate text-base font-bold text-white">{view.title}</p>}
                    {view.preview && <p className="mt-0.5 line-clamp-2 break-words text-sm text-white">{view.preview}</p>}
                    {view.chip && <div className="mt-1"><StatusChip tone={view.chip.tone}>{view.chip.label}</StatusChip></div>}
                    {expanded && view.messages?.length > 1 && (
                        <ol id={`notification-messages-${timerKey}`} className="mt-2 max-h-40 space-y-2 overflow-y-auto border-t border-white/20 pt-2 text-xs text-white">
                            {view.messages.map((message) => (
                                <li key={message.key} className="border-b border-white/10 pb-2 last:border-0">
                                    <button
                                        type="button"
                                        className="w-full text-left hover:underline"
                                        onClick={(event) => { event.stopPropagation(); onOpenMessage?.(message.key); }}
                                        aria-label={`Open ${message.title}${message.preview ? `: ${message.preview}` : ''}`}
                                    >
                                        {message.preview || message.title}
                                        {message.time && <span className="ml-2 text-white/70">{message.time}</span>}
                                    </button>
                                </li>
                            ))}
                        </ol>
                    )}
                    {expanded && count > 1 && (
                        <button
                            type="button"
                            className="mt-2 text-xs font-semibold text-white underline"
                            aria-label="Restack notification messages"
                            onClick={(event) => { event.stopPropagation(); setExpanded(false); }}
                        >
                            Restack
                        </button>
                    )}
                    {view.url && (
                        <div className="mt-2 flex gap-2">
                            <Button3D className="ia-popup3d__open" onClick={(event) => { event.stopPropagation(); onOpen?.(); }}>Open</Button3D>
                            <Button3D className="ia-popup3d__reply" onClick={(event) => { event.stopPropagation(); onReply?.(); }}>Reply</Button3D>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onDismiss?.('close'); }}
                    aria-label="Dismiss notification"
                    className="ia-popup3d__close flex-none rounded-md px-1.5 text-lg leading-none text-white hover:bg-white/10"
                >
                    ×
                </button>
            </div>
        </div>
    );
}
